import { FilterDropdown, TagField, TextField, useSelect, useTable } from "@refinedev/antd";
import { Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ILicence, IMetadataOther, IVessel } from "interfaces";
import React from "react";
import { getBooleanTagColor } from "constants/tagColors";
import {
  OTHER_SERVICES_BY_PRODUCT,
  OtherServiceProductGroup,
  readOtherField,
} from "components/metadata/otherServiceCatalog";

const isProductActive = (
  group: OtherServiceProductGroup,
  others: IMetadataOther | null | undefined,
): boolean =>
  group.services.some(
    (svc) => readOtherField<string>(others, `${svc.prefix}Status`) === "active",
  );

const productGroup = (name: string): OtherServiceProductGroup => {
  const g = OTHER_SERVICES_BY_PRODUCT.find((x) => x.product === name);
  if (!g) throw new Error(`Unknown other-service product: ${name}`);
  return g;
};

const ROLLUP_PRODUCTS: Array<{ title: string; key: string; width: number }> = [
  { title: "Standard Library Publications", key: "standardLibraryPublications", width: 110 },
  { title: "eMariner Planning Station", key: "planningStation", width: 130 },
  { title: "eMariner eNTM", key: "eNtmSupport", width: 100 },
  { title: "AVCS Optimization Services", key: "avcsOptimization", width: 120 },
];

export const ServiceSubscription: React.FC = () => {
  const { tableProps } = useTable<ILicence>({
    resource: "licence",
    pagination: {
      pageSize: 10
    },
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<ILicence> = [
    {
      title: "IMO Number",
      dataIndex: ["vessel", "imoNumber"],
      key: "vessel.imoNumber",
      fixed: "left",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      fixed: "left",
      width: 150,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "AVCS",
      dataIndex: ["subscription", "metadataAvcs"],
      key: "avcs",
      width: 80,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    {
      title: "ADP",
      dataIndex: ["subscription", "metadataAdp"],
      key: "adp",
      width: 80,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    {
      title: "e-NP",
      dataIndex: ["subscription", "metadataEnp"],
      key: "enp",
      width: 80,
      align: "center",
      render: (value) => (
        <TagField
          color={getBooleanTagColor(!!value)}
          value={value ? "yes" : "no"}
        />
      ),
    },
    ...ROLLUP_PRODUCTS.map((p) => ({
      title: p.title,
      key: p.key,
      width: p.width,
      align: "center" as const,
      render: (_: unknown, record: ILicence) => {
        const active = isProductActive(
          productGroup(p.title),
          record?.subscription?.metadataOthers,
        );
        return (
          <TagField
            color={getBooleanTagColor(active)}
            value={active ? "yes" : "no"}
          />
        );
      },
    })),
  ];

  return (
    <Table
      {...tableProps}
      columns={columns}
      rowKey="id"
      size="middle"
      scroll={{ x: "max-content" }}
      sticky
      pagination={{
        ...tableProps.pagination,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
      }}
    />
  );
};
